import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, Check } from 'lucide-react';

const features = [
  {
    id: 'confidence-score',
    badge: '01',
    title: 'AI speed. Production confidence.',
    subtitle: 'A confidence score on every build.',
    gradient: 'from-sunset-coral-400 to-soft-lavender-500',
    description: 'Joylo is the AI builder for founders who need their app to actually work, with paying users, real traffic, and the boring stuff handled. Every build is scored on what actually breaks in production. When the score is green, you ship. When it\'s not, you know exactly why, and what to do about it.\n→\nSee exactly where the AI is shaky, before your users do.',
    highlights: [
      'Production-readiness signal on every build',
      'Catches what\'s risky before your users do',
      'The intelligence behind our production guarantee'
    ],
    cta: 'See Your Score',
    accentColor: 'sunset-coral'
  },
  {
    id: 'real-engineers',
    badge: '02',
    title: 'Real Engineers. One Click Away.',
    subtitle: 'Real engineers. Real delivery experience. One button away.',
    gradient: 'from-soft-lavender-500 to-sunset-coral-400',
    description: 'When AI hits a wall and it always does every other tool just tells you to "try a different prompt." Joylo connects you in one click to one of our in-house, full-time engineers. You get an engineer who\'s already in your codebase and on the same team that built the platform underneath. They jump in, finish what AI started, and hand you back something that\'s ready to ship.\n→\nNo more dead ends. Just an engineer who ships.',
    highlights: [
      'Real engineers on demand, not a marketplace',
      'Already familiar with your project. No onboarding tax. Zero context handoff.',
      'Flexible monthly Expert Assist plans. Use it when you need it.'
    ],
    cta: 'Meet Your Engineer',
    accentColor: 'soft-lavender'
  },
  {
    id: 'production-support',
    badge: '03',
    title: 'Production Support',
    subtitle: 'We stay where others leave.',
    gradient: 'from-sunset-coral-400 to-soft-lavender-500',
    description: 'Most AI builders go quiet after you deploy. That\'s where Joylo gets serious. Expert Assist is one button on your dashboard. Press it any time, for anything: a build that\'s stuck, a feature you can\'t finish, a production issue real users surfaced. Same engineers. Same fixed price. From day one to day one thousand.\n→\nLaunch Isn\'t the Finish. It\'s Day One.',
    highlights: [
      'Post-launch support from engineers who know the platform inside out',
      'One button to press for fixes, features, deploys, or debugging',
      'One team to call when something breaks. Not five.'
    ],
    cta: 'Build for Production',
    accentColor: 'sunset-coral'
  },
  {
    id: 'full-ownership',
    badge: '04',
    title: 'Own Everything You Build',
    subtitle: 'Your app. Yours to own, yours to scale.',
    gradient: 'from-soft-lavender-500 to-sunset-coral-400',
    description: 'Joylo apps ship production-ready from day one with solid architecture, secure defaults, hosting that doesn\'t fold the first time traffic spikes. And every line of code is yours. Export it whenever you want. Keep it running with us because it\'s easier, not because it\'s locked in.\n→\nYour code. Your rules. No strings attached.',
    highlights: [
      'Enterprise-grade hosting, security, and architecture by default',
      'Full code ownership. Export anytime, no proprietary runtime.',
      'We earn your stay every day. We never trap you into it.'
    ],
    cta: 'Own Your Future',
    accentColor: 'soft-lavender'
  }
];

export default function Features() {
  const [activeFeature, setActiveFeature] = useState(0);
  const [expandedMobile, setExpandedMobile] = useState<string | null>(null);

  const toggleMobileExpansion = (featureId: string) => {
    setExpandedMobile(expandedMobile === featureId ? null : featureId);
  };

  const renderDescription = (description: string, isFirstFeature: boolean = false) => {
    // Check if description contains arrow separator for secondary heading
    const hasArrow = description.includes('→');

    if (hasArrow) {
      const [main, secondary] = description.split('→');
      return (
        <div className="mb-6">
          <div className="mb-4">
            <h3 className="text-2xl lg:text-3xl font-bold text-gradient-sunset leading-tight">
              {secondary.trim()}
            </h3>
          </div>

          <p className="text-lg text-soft-gray-600 leading-relaxed mb-6">
            {main.trim()}
          </p>
        </div>
      );
    }

    return (
      <p className="text-lg text-soft-gray-600 leading-relaxed mb-6">
        {description}
      </p>
    );
  };

  return (
    <section className="py-10 sm:py-20 bg-gradient-to-b from-warm-cream-50 to-white">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        <div className="text-left sm:text-center mb-8 sm:mb-16">
          <div className="inline-flex items-center gap-2 glass px-3 sm:px-4 py-2 rounded-full mb-5 sm:mb-6">
            <span className="text-xs sm:text-sm font-medium text-soft-lavender-600">Why Joylo is Different</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-ink-blue-900 mb-3 sm:mb-4 leading-[1.15]">
            Everyone talks about building apps with AI. We talk about finishing them.
          </h2>
        </div>

        {/* Desktop Layout */}
        <div className="hidden lg:grid lg:grid-cols-2 lg:gap-16">
          {/* Left Panel - Navigation */}
          <div className="space-y-4">
            {features.map((feature, index) => (
              <motion.div
                key={feature.id}
                className={`p-6 rounded-2xl cursor-pointer transition-all duration-300 ${
                  activeFeature === index
                    ? 'bg-white shadow-elegant-lg border-2 border-warm-cream-300'
                    : 'glass hover:bg-white hover:shadow-elegant'
                }`}
                onClick={() => setActiveFeature(index)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex items-center space-x-4">
                  {/* Subtle number indicator */}
                  <div className="flex-shrink-0">
                    <span className="text-sm font-medium text-soft-gray-400 tracking-wider">
                      {feature.badge}
                    </span>
                  </div>
                  
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-ink-blue-900 mb-1">
                      {feature.title}
                    </h3>
                    <p className="text-soft-gray-600 text-sm">
                      {feature.subtitle}
                    </p>
                  </div>
                  <ChevronRight className={`w-5 h-5 text-soft-gray-400 transition-transform ${
                    activeFeature === index ? 'rotate-90' : ''
                  }`} />
                </div>
              </motion.div>
            ))}
          </div>

          {/* Right Panel - Details */}
          <div className="sticky top-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeFeature}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="bg-white rounded-3xl p-8 shadow-elegant-xl border border-warm-cream-200"
              >
                <div className="mb-6">
                  {/* Subtle badge indicator */}
                  <div className="inline-flex items-center gap-2 mb-6">
                    <span className="text-xs font-medium text-soft-gray-400 tracking-widest uppercase">
                      {features[activeFeature].badge}
                    </span>
                    <div className="h-px w-8 bg-gradient-sunset"></div>
                  </div>
                  
                  {renderDescription(features[activeFeature].description, activeFeature === 0)}
                </div>

                <div className="space-y-4 mb-8">
                  {features[activeFeature].highlights.map((highlight, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <Check className="w-5 h-5 text-soft-lavender-500 mt-0.5 flex-shrink-0" />
                      <span className="text-ink-blue-800 font-medium">{highlight}</span>
                    </div>
                  ))}
                </div>

                <motion.a
                  href="https://joylo.io"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`block w-full py-4 px-6 bg-gradient-to-r ${features[activeFeature].gradient} text-white font-semibold rounded-xl hover:shadow-elegant-lg transition-all duration-200 text-center`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {features[activeFeature].cta}
                </motion.a>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Mobile Layout */}
        <div className="lg:hidden space-y-4">
          {features.map((feature, index) => (
            <div key={feature.id}>
              {/* Feature Card */}
              <motion.div
                className={`p-6 rounded-2xl cursor-pointer transition-all duration-300 ${
                  expandedMobile === feature.id
                    ? 'bg-white shadow-elegant-lg border-2 border-warm-cream-300'
                    : 'glass hover:bg-white hover:shadow-elegant'
                }`}
                onClick={() => toggleMobileExpansion(feature.id)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex items-center space-x-4">
                  {/* Subtle number indicator */}
                  <div className="flex-shrink-0">
                    <span className="text-sm font-medium text-soft-gray-400 tracking-wider">
                      {feature.badge}
                    </span>
                  </div>
                  
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-ink-blue-900 mb-1">
                      {feature.title}
                    </h3>
                    <p className="text-soft-gray-600 text-sm">
                      {feature.subtitle}
                    </p>
                  </div>
                  <ChevronRight className={`w-5 h-5 text-soft-gray-400 transition-transform ${
                    expandedMobile === feature.id ? 'rotate-90' : ''
                  }`} />
                </div>
              </motion.div>

              {/* Expanded Content */}
              <AnimatePresence>
                {expandedMobile === feature.id && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="bg-white rounded-2xl p-6 mt-2 shadow-elegant border border-warm-cream-200">
                      {renderDescription(feature.description, index === 0)}

                      <div className="space-y-3 mb-6">
                        {feature.highlights.map((highlight, highlightIndex) => (
                          <div key={highlightIndex} className="flex items-start space-x-3">
                            <Check className="w-5 h-5 text-soft-lavender-500 mt-0.5 flex-shrink-0" />
                            <span className="text-ink-blue-800 font-medium text-sm">{highlight}</span>
                          </div>
                        ))}
                      </div>

                      <motion.a
                        href="https://joylo.io"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`block w-full py-3 px-6 bg-gradient-to-r ${feature.gradient} text-white font-semibold rounded-xl hover:shadow-elegant-lg transition-all duration-200 text-center`}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        {feature.cta}
                      </motion.a>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
